// Settings › Markup & fitting's pricing settings and product markups (UC-PRICING-006, UC-PRICING-005). The settings — the rounding
// of frame bands and product markups, the rounding of lens lines, and whether a receipt proposes a price (BR-PRICING-018) — are
// shown as they resolve at the acting branch, each with where its value comes from; Change sets one for a scope the reader may
// write: the organisation's through `/catalogue-settings` (`catalogue.settings.write`: Owner, Admin), a branch's own through the
// setting store (`tenancy.settings.write`: the Owner, or a Branch Owner for the branches they own, D40), or a branch back to the
// organisation's value. The price-override floor is the organisation's discount thresholds (BR-PRICING-017), named here and
// kept in Settings › General. The product markups are every live one, the organisation's and each branch's, each opening the
// product whose Markup panel changes it. At a practice of one store (UC-PRICING-006 A6, UC-PRICING-005 A6 step 3; DVN's answers of
// 28 Oct 2026, change 13) a value is changed for the organisation with no choice of scope — unless the store holds one of its own,
// set before, which Change offers to put back, or the reader may set only the store's — and the markups name no scope while none
// listed is the store's own; a branch closed since charges nothing, so its markups are not listed.
import type { ProductMarkupView, RoundingRule, SettingSource } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { catalogueSettings, effectiveSettings } from "../api/endpoints.catalogue";
import { settingStore } from "../api/endpoints";
import { pricing } from "../api/endpoints.pricing";
import { describeError } from "../api/problem";
import { formatDate, formatMoney, percentFromBp } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, canEverywhere, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Empty, ErrorAlert, Loading, Panel, Select } from "../ui/bits";
import { ROUNDING_LABEL, byLine, markupText, possessive, sourceWord, usePricingSettings } from "./PricingShared";

type Key = "roundingRule" | "lensRoundingRule" | "proposePriceAtReceipt";
const SETTINGS: readonly { key: Key; label: string; help: string }[] = [
  { key: "roundingRule", label: "Rounding — frames and product markups", help: "How a price a frame band or a product markup computes is rounded. A price typed by hand is never rounded." },
  { key: "lensRoundingRule", label: "Rounding — lens lines", help: "How a lens price derived from a lab's price list is rounded." },
  { key: "proposePriceAtReceipt", label: "Propose a price at receipt", help: "Whether a goods receipt proposes each line's shelf price from its band or markup." },
];
const RULES = Object.keys(ROUNDING_LABEL) as RoundingRule[];
const INHERIT = "__organisation__";

const valueWords = (key: Key, v: unknown): string => (key === "proposePriceAtReceipt" ? (v === false ? "Off" : "On") : typeof v === "string" && v in ROUNDING_LABEL ? ROUNDING_LABEL[v as RoundingRule] : String(v ?? "—"));
const encode = (key: Key, v: unknown): string => (key === "proposePriceAtReceipt" ? (v === false ? "off" : "on") : String(v ?? "NearestR1"));
const decode = (key: Key, s: string): unknown => (key === "proposePriceAtReceipt" ? s === "on" : s);

/** Which scopes the reader may set a pricing setting at (BR-PRICING-018): the organisation, every branch, or the branches they own. */
function useWritableScopes(): { id: string; name: string }[] {
  const s = useSessionView();
  return useMemo(() => {
    if (!s) return [];
    const out: { id: string; name: string }[] = [];
    if (can(s, "catalogue.settings.write")) out.push({ id: "org", name: "the organisation" });
    const everywhere = canEverywhere(s, "tenancy.settings.write");
    const owned = new Set(s.branchScopedPermissions.find((p) => p.code === "tenancy.settings.write")?.branchIds ?? []);
    for (const b of s.switchableBranches) if (b.status === "Active" && (everywhere || owned.has(b.id))) out.push({ id: b.id, name: b.name });
    return out;
  }, [s]);
}

export function PricingSettingsPanel({ branchId, branchName }: { branchId: string | null; branchName: string | null }) {
  const s = useSessionView();
  const here = usePricingSettings(branchId ?? "org");
  const effective = useQuery({ queryKey: ["settings", "effective"], queryFn: () => effectiveSettings.get(), staleTime: 60_000 });
  const writable = useWritableScopes();
  const oneStore = useOneStore();
  const block = useWriteBlock();
  const [editing, setEditing] = useState<Key | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const sales = effective.data?.data.settings.sales;
  const pct = typeof sales?.discountPinThresholdPercentBp?.value === "number" ? sales.discountPinThresholdPercentBp.value : s?.settings.discountPinThresholdPercentBp ?? 0;
  const rands = typeof sales?.discountPinThresholdCents?.value === "number" ? sales.discountPinThresholdCents.value : s?.settings.discountPinThresholdCents ?? 0;
  const values = here.data?.data.settings;
  /**
   * The scopes Change offers for a setting: every one the reader may write — at a practice of one store the organisation's alone,
   * the store's too only where it holds a value of its own to put back, or where the reader may set the store's and not the
   * organisation's (a Branch Owner, D40).
   */
  const scopesFor = (key: Key): { id: string; name: string }[] => {
    if (!oneStore) return writable;
    const org = writable.filter((x) => x.id === "org");
    if (org.length === 0) return writable;
    return values?.[key]?.source === "branch" ? writable.filter((x) => x.id === "org" || x.id === branchId) : org;
  };
  return (
    <Panel title="Pricing settings" meta={branchName ? `as ${branchName} resolves them` : "the organisation's"}
      foot={<span className="help">A price below the {oneStore ? "price in force" : "branch price"} needs a Control PIN beyond the discount thresholds of <Link to="/settings/general">Settings › General</Link> ({percentFromBp(pct)} · {formatMoney(rands)}). A change applies to later prices only; no price already set changes.</span>}>
      {notice && <div aria-live="polite">{notice}</div>}
      {here.isPending && <Loading what="the pricing settings" />}
      {here.isError && <ErrorAlert error={here.error} />}
      {values && (
        <div className="list">
          {SETTINGS.map((d) => {
            const held = values[d.key];
            const scopes = scopesFor(d.key);
            return (
              <div key={d.key} className="item" style={{ flexWrap: "wrap" }}>
                <div className="grow"><div className="primary">{d.label}</div><div className="sub">{d.help}</div></div>
                <span className="small" data-setting={d.key}><b>{valueWords(d.key, held?.value)}</b> · <span className="faint">{sourceWord((held?.source ?? "default") as SettingSource, branchName)}</span></span>
                {scopes.length > 0 && editing !== d.key && (
                  <button className="btn sm" type="button" disabled={block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} aria-label={`Change ${d.label}`} onClick={() => { setNotice(null); setEditing(d.key); }}>Change</button>
                )}
                {editing === d.key && (
                  <SettingEditor setting={d} scopes={scopes} branchId={branchId} oneStore={oneStore} storeOwn={oneStore && held?.source === "branch" && branchName ? { store: branchName, value: valueWords(d.key, held.value) } : null} onClose={() => setEditing(null)} onSaved={(words) => { setEditing(null); setNotice(<Alert tone="ok">{words}</Alert>); }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/**
 * One setting changed at one scope: the scope, the value — or, at a branch, the organisation's again — then Save (Enter). With one
 * scope to write there is no choice to offer, and at a practice of one store the notice names no other branch (UC-PRICING-006 A6) —
 * and says the store keeps a value of its own when the organisation's is changed (`storeOwn`; A6 step 3; the verification of 30 Oct
 * 2026, C13-3).
 */
function SettingEditor({ setting, scopes, branchId, oneStore, storeOwn, onClose, onSaved }: { setting: (typeof SETTINGS)[number]; scopes: readonly { id: string; name: string }[]; branchId: string | null; oneStore: boolean; storeOwn: { store: string; value: string } | null; onClose: () => void; onSaved: (words: ReactNode) => void }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState(scopes[0]!.id);
  const atScope = usePricingSettings(scope);
  const org = usePricingSettings("org");
  const held = atScope.data?.data.settings[setting.key];
  const current = scope !== "org" && held?.source !== "branch" ? INHERIT : encode(setting.key, held?.value);
  const [choice, setChoice] = useState<string | null>(null);
  useEffect(() => { setChoice(null); }, [scope]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const picked = choice ?? current;
  const orgValue = org.data?.data.settings[setting.key]?.value;
  const scopeName = scopes.find((x) => x.id === scope)?.name ?? "the branch";
  const save = async () => {
    if (busy || !held || picked === current) return;
    setBusy(true);
    setError(null);
    try {
      if (scope === "org") await catalogueSettings.patch({ pricing: { [setting.key]: decode(setting.key, picked) } });
      else if (picked === INHERIT) await settingStore.removeOverride(scope, "pricing", setting.key);
      else await settingStore.save(scope, "pricing", { [setting.key]: decode(setting.key, picked) });
      await Promise.all([qc.invalidateQueries({ queryKey: ["settings"] }), qc.invalidateQueries({ queryKey: ["catalogue-settings"] })]);
      onSaved(<><b>{setting.label}: {picked === INHERIT ? `${scopeName} follows the organisation again` : `${valueWords(setting.key, decode(setting.key, picked))} for ${scopeName}`}.</b> {oneStore ? (scope === "org" && storeOwn ? `It applies within a minute, but ${storeOwn.store} keeps its own, ${storeOwn.value}, until an Owner puts it back to the organisation's.` : "It applies within a minute.") : <>Every branch it reaches follows within a minute{scope !== "org" && scope !== branchId ? "; this page shows the acting branch" : ""}.</>}</>);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <form className="row c gap-8" style={{ flexBasis: "100%", flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); void save(); }} aria-label={`Change ${setting.label}`}>
      {(!oneStore || scopes.length > 1) && (
        <Select aria-label="For" value={scope} autoFocus onChange={(e) => setScope(e.target.value)}>
          {scopes.map((x) => <option key={x.id} value={x.id}>{x.id === "org" ? "Organisation" : x.name}</option>)}
        </Select>
      )}
      <Select aria-label={setting.label} value={picked} disabled={!held} autoFocus={oneStore && scopes.length === 1} onChange={(e) => setChoice(e.target.value)}>
        {scope !== "org" && <option value={INHERIT}>As the organisation ({valueWords(setting.key, orgValue)})</option>}
        {setting.key === "proposePriceAtReceipt"
          ? <><option value="on">On</option><option value="off">Off</option></>
          : RULES.map((r) => <option key={r} value={r}>{ROUNDING_LABEL[r]}</option>)}
      </Select>
      <button className="btn primary sm" type="submit" disabled={busy || !held || picked === current}>{busy ? "Saving…" : "Save"}</button>
      <button className="btn sm" type="button" onClick={onClose}>Cancel</button>
      {error !== null && <div className="error" role="alert" style={{ flexBasis: "100%" }}>{describeError(error)}</div>}
    </form>
  );
}

/** Every live product markup, the organisation's and each branch's, grouped by product (UC-PRICING-005; changed on the product). */
export function ProductMarkupsPanel() {
  const s = useSessionView();
  const oneStore = useOneStore();
  const list = useQuery({ queryKey: ["pricing", "product-markups", "all"], queryFn: () => pricing.productMarkups() });
  const rows = useMemo(() => [...(list.data?.data.items ?? [])].filter((m) => !oneStore || m.branchId === null || m.branchId === s?.activeBranchId).sort((a, b) => (a.product?.sku ?? "").localeCompare(b.product?.sku ?? "") || Number(a.branchId !== null) - Number(b.branchId !== null) || (a.branch?.name ?? "").localeCompare(b.branch?.name ?? "")), [list.data, oneStore, s?.activeBranchId]);
  const products = new Set(rows.map((m) => m.productId)).size;
  // The scope is named while a markup listed is a branch's: always at a practice of several branches.
  const scoped = !oneStore || rows.some((m) => m.branchId !== null);
  return (
    <Panel title="Product markups" meta={list.data ? `${products} product${products === 1 ? "" : "s"} · contact lenses, accessories and stocked lenses` : undefined} tight
      foot={<span className="help">{oneStore ? "" : "A branch's own markup wins there over the organisation's. "}Each is changed on its product's Markup panel, where the cost shows the price it gives.</span>}>
      {list.isPending && <div className="panel-body"><Loading what="the product markups" /></div>}
      {list.isError && <div className="panel-body"><ErrorAlert error={list.error} /></div>}
      {list.data && (rows.length === 0 ? <Empty title="No product markups yet.">A contact lens's, an accessory's or a stocked lens's markup is set on its panel under Products.</Empty> : (
        <table>
          <thead><tr><th>Product</th>{scoped && <th>Applies to</th>}<th className="r">Markup</th><th>Set by</th></tr></thead>
          <tbody>
            {rows.map((m: ProductMarkupView) => (
              <tr key={m.id}>
                <td>{m.product ? <><Link to={`/products/${m.product.id}`}><span className="mono">{m.product.sku}</span></Link> {m.product.name}</> : <span className="faint">—</span>}</td>
                {scoped && <td>{m.branch ? `${possessive(m.branch.name)} own` : "Organisation"}</td>}
                <td className="mono r">{markupText(m.percentBp, m.valueCents)}</td>
                <td className="nw">{byLine(m.createdBy)}<div className="small faint">{formatDate(m.updatedAt)}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </Panel>
  );
}
